import React from 'react';
import { Search, Filter, X, Zap } from 'lucide-react';
import { Format, CECategory, CE_CATEGORIES } from '../../data/classes';
interface FindAClassFiltersProps {
  formatFilter: Format | 'All Formats';
  setFormatFilter: (format: Format | 'All Formats') => void;
  categoryFilter: CECategory | 'All Categories';
  setCategoryFilter: (category: CECategory | 'All Categories') => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  onReset: () => void;
  onShowNext: () => void;
  resultCount: number;
}
export function FindAClassFilters({
  formatFilter,
  setFormatFilter,
  categoryFilter,
  setCategoryFilter,
  searchQuery,
  setSearchQuery,
  onReset,
  onShowNext,
  resultCount
}: FindAClassFiltersProps) {
  const quickFilters: Array<{
    label: string;
    value: Format | 'All Formats';
  }> = [{
    label: 'All',
    value: 'All Formats'
  }, {
    label: 'Zoom',
    value: 'Zoom'
  }, {
    label: 'In-Person',
    value: 'In-Person'
  }];
  return <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-8" data-id="element-173">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6" data-id="element-174">
        <div className="flex items-center" data-id="element-175">
          <Filter className="w-5 h-5 text-brand-purple-600 mr-2" data-id="element-176" />
          <h2 className="text-xl font-bold text-gray-900" data-id="element-177">Find a Class</h2>
        </div>

        {/* Quick Format Filters */}
        <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50 self-start sm:self-auto" role="group" aria-label="Quick format filter" data-id="element-178">
          {quickFilters.map(qf => <button key={qf.value} onClick={() => setFormatFilter(qf.value)} aria-pressed={formatFilter === qf.value} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple-500 ${formatFilter === qf.value ? 'bg-brand-purple-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-179">
              {qf.label}
            </button>)}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-6" data-id="element-180">
        {/* Search */}
        <div className="md:col-span-12 lg:col-span-4" data-id="element-181">
          <label htmlFor="search-classes" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-182">
            Search
          </label>
          <div className="relative" data-id="element-183">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-184">
              <Search className="h-5 w-5 text-gray-400" data-id="element-185" />
            </div>
            <input type="text" id="search-classes" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-brand-purple-500 focus:border-brand-purple-500 sm:text-sm" placeholder="Search by class title, instructor, city, or topic" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-186" />
          </div>
        </div>

        {/* Format Filter */}
        <div className="md:col-span-6 lg:col-span-3" data-id="element-187">
          <label htmlFor="format-filter" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-188">
            Class Format
          </label>
          <select id="format-filter" className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-brand-purple-500 focus:border-brand-purple-500 sm:text-sm rounded-md" value={formatFilter} onChange={e => setFormatFilter(e.target.value as Format | 'All Formats')} data-id="element-189">
            <option value="All Formats" data-id="element-190">All Formats</option>
            <option value="In-Person" data-id="element-191">In-Person</option>
            <option value="Zoom" data-id="element-192">Zoom</option>
          </select>
        </div>

        {/* Category Filter */}
        <div className="md:col-span-6 lg:col-span-5" data-id="element-193">
          <label htmlFor="category-filter" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-194">
            CE Category
          </label>
          <select id="category-filter" className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-brand-purple-500 focus:border-brand-purple-500 sm:text-sm rounded-md" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value as CECategory | 'All Categories')} data-id="element-195">
            <option value="All Categories" data-id="element-196">All Categories</option>
            {CE_CATEGORIES.map(cat => <option key={cat} value={cat} data-id="element-197">
                {cat}
              </option>)}
          </select>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pt-4 border-t border-gray-100" data-id="element-198">
        <div className="flex flex-wrap items-center gap-4" data-id="element-199">
          <span className="text-sm text-gray-600" data-id="element-200">
            Showing{' '}
            <span className="font-bold text-gray-900" data-id="element-201">{resultCount}</span>{' '}
            upcoming classes
          </span>
          <button onClick={onShowNext} className="inline-flex items-center text-sm font-semibold text-brand-purple-700 hover:text-brand-purple-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple-500 rounded" data-id="element-202">
            <Zap className="w-4 h-4 mr-1" data-id="element-203" />
            Show Next Available Classes
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-id="element-204">
          <div className="flex items-center text-sm text-gray-600" data-id="element-205">
            <span className="w-3 h-3 rounded-full bg-brand-purple-600 mr-1.5" data-id="element-206"></span>
            Jon Kichen
            <span className="w-3 h-3 rounded-full bg-brand-orange-500 ml-4 mr-1.5" data-id="element-207"></span>
            Other Instructors
          </div>

          <button onClick={onReset} className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-purple-500" data-id="element-208">
            <X className="w-4 h-4 mr-1" data-id="element-209" />
            Reset Filters
          </button>
        </div>
      </div>
    </div>;
}